import React, { useCallback, useEffect, useState } from "react"
import { useDispatch } from "react-redux"
import {
  Accordion,
  AccordionBody,
  AccordionHeader,
  AccordionItem,
  Col,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap"
import * as Yup from "yup"
import { useFormik } from "formik"
import FormLabel from "components/Extra/FormLabel"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { createDeliveryMan, updateDeliveryMan } from "../../../../store/delivery-men/actions"
import { DeliveryMan } from "../DeliveryMen"

interface AddNewDeliveryManProps {
  isOpen: boolean
  toggle: () => void
  deliveryMan: DeliveryMan | null
}

export const AddDeliveryMan: React.FC<AddNewDeliveryManProps> = ({
  isOpen,
  toggle,
  deliveryMan,
}) => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const isEditMode = deliveryMan !== null

  const [openFirstAccordion, setOpenFirstAccordion] = useState("0")
  const [openSecondAccordion, setOpenSecondAccordion] = useState("1")

  const validation = useFormik({
    enableReinitialize: true,
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    initialValues: {
      name: deliveryMan?.name || "",
      mobile: deliveryMan?.mobile || "",
      address: deliveryMan?.address || "",
      overview: deliveryMan?.overview || "",
      isActive: deliveryMan?.isActive ?? true,
    },
    validationSchema: Yup.object({
      name: Yup.string()
        .trim()
        .required(t("deliveryMen_deliveryManNameValidationMessage") as string),
      mobile: Yup.string()
        .trim()
        .required(t("deliveryMen_mobileNumberValidationMessage") as string),
      address: Yup.string()
        .trim()
        .required(t("deliveryMen_addressValidationMessage") as string),
      overview: Yup.string().trim(),
      isActive: Yup.boolean(),
    }),
    onSubmit: values => {
      const deliveryManData = {
        name: values.name,
        mobile: values.mobile,
        address: values.address || "",
        overview: values.overview || "",
        isActive: values.isActive,
      }

      if (isEditMode && deliveryMan) {
        dispatch(updateDeliveryMan(deliveryMan.id, deliveryManData, { push: navigate }))
      } else {
        dispatch(createDeliveryMan(deliveryManData, { push: navigate }))
      }

      validation.resetForm()
      toggle()
    },
  })

  useEffect(() => {
    validation.resetForm()
  }, [isOpen, deliveryMan])

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg">
      <ModalHeader toggle={toggle} tag="h3">
        <h4 className="text-capitalize fw-semibold font-size-24">
          {isEditMode ? t("common_edit") : t("deliveryMen_addDeliveryMan")}
        </h4>
      </ModalHeader>
      <ModalBody>
        <Form
          onSubmit={e => {
            e.preventDefault()
            validation.handleSubmit()
            return false
          }}
        >
          <Accordion open={openFirstAccordion}>
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenFirstAccordion(openFirstAccordion === "0" ? "1" : "0")
                }
              >
                {t("common_basicInfo")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup>
                  <FormLabel htmlFor="name" required>
                    {t("deliveryMen_deliveryManName")}
                  </FormLabel>

                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("deliveryMen_enterDeliveryName") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.name || ""}
                    invalid={
                      validation.touched.name &&
                      validation.errors.name
                        ? true
                        : false
                    }
                  />
                  {validation.touched.name &&
                  validation.errors.name ? (
                    <FormFeedback type="invalid">
                      {validation.errors.name}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="mobile" required>
                    {t("deliveryMen_mobileNumber")}
                  </FormLabel>
                  <Input
                    id="mobile"
                    name="mobile"
                    type="text"
                    placeholder={t("deliveryMen_enterMobile") as string}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.mobile || ""}
                    invalid={
                      validation.touched.mobile &&
                      validation.errors.mobile
                        ? true
                        : false
                    }
                  />
                  {validation.touched.mobile &&
                  validation.errors.mobile ? (
                    <FormFeedback type="invalid">
                      {validation.errors.mobile}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="address" required>
                    {t("deliveryMen_address")}
                  </FormLabel>
                  <Input
                    id="address"
                    name="address"
                    type="text"
                    placeholder={
                      t("deliveryMen_enterDeliveryAddress") as string
                    }
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.address || ""}
                    invalid={
                      validation.touched.address && validation.errors.address
                        ? true
                        : false
                    }
                  />
                  {validation.touched.address && validation.errors.address ? (
                    <FormFeedback type="invalid">
                      {validation.errors.address}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
                <FormGroup>
                  <FormLabel htmlFor="overview">
                    {t("deliveryMen_deliveryPartnerOverview")}
                  </FormLabel>
                  <Input
                    id="overview"
                    name="overview"
                    type="textarea"
                    placeholder={
                      t("deliveryMen_enterDeliveryPartnerOverview") as string
                    }
                    rows={3}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                    value={validation.values.overview || ""}
                    invalid={
                      validation.touched.overview && validation.errors.overview
                        ? true
                        : false
                    }
                  />
                  {validation.touched.overview && validation.errors.overview ? (
                    <FormFeedback type="invalid">
                      {validation.errors.overview}
                    </FormFeedback>
                  ) : null}
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Accordion open={openSecondAccordion} className="mt-4">
            <AccordionItem>
              <AccordionHeader
                targetId="0"
                onClick={() =>
                  setOpenSecondAccordion(
                    openSecondAccordion === "0" ? "1" : "0"
                  )
                }
              >
                {t("deliveryMen_activeStatus")}
              </AccordionHeader>
              <AccordionBody accordionId="0">
                <FormGroup>
                  <FormGroup className="d-flex align-items-center gap-3">
                    <FormLabel htmlFor="isActive">
                      <span className="fw-bold">{t("deliveryMen_active")}</span>{" "}
                      ({t("deliveryMen_deliveryManVisibility")})
                    </FormLabel>
                    <div className="form-check form-switch mb-2">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="isActive"
                        name="isActive"
                        checked={validation.values.isActive}
                        onChange={validation.handleChange}
                        onBlur={validation.handleBlur}
                      />
                    </div>
                  </FormGroup>
                </FormGroup>
              </AccordionBody>
            </AccordionItem>
          </Accordion>
          <Row>
            <Col>
              <div className="d-flex justify-content-end gap-2 mt-4">
                <button
                  type="submit"
                  className={`btn btn-primary save-customer`}
                  disabled={!(validation.isValid && validation.dirty)}
                >
                  {isEditMode ? t("common_save") : t("common_create")}
                </button>
                <button
                  onClick={toggle}
                  type="button"
                  className="btn btn-cancel text-primary border border-primary"
                >
                  {t("common_cancel")}
                </button>
              </div>
            </Col>
          </Row>
        </Form>
      </ModalBody>
    </Modal>
  )
}
